import React, { useState, useEffect, useRef } from 'react';
import { Download, Play, Pause, Lock, Layers } from 'lucide-react';
import { api } from '../services/api';

const OVERLAY_CATEGORIES = [
  { id: 'all',        name: 'Todas',         icon: '🎬' },
  { id: 'glitch',     name: 'Glitch',        icon: '⚡' },
  { id: 'light',      name: 'Light Leaks',   icon: '✨' },
  { id: 'particles',  name: 'Partículas',    icon: '🌟' },
  { id: 'smoke',      name: 'Smoke & Fog',   icon: '🌫️' },
  { id: 'transitions',name: 'Transições',    icon: '🔀' },
  { id: 'lens',       name: 'Lens Flare',    icon: '🔆' },
  { id: 'grain',      name: 'Film Grain',    icon: '🎞️' },
  { id: 'outros',     name: 'Outros',        icon: '📁' },
];

const OverlaysView = () => {
  const [overlays, setOverlays]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [hasAccess, setHasAccess]         = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch]               = useState('');
  const [playingId, setPlayingId]         = useState(null);
  const [downloading, setDownloading]     = useState(null);
  const videoRefs                         = useRef({});

  useEffect(() => {
    checkAccessAndLoad();
  }, []);

  const checkAccessAndLoad = async () => {
    try {
      const access = await api.checkSFXAccess();
      setHasAccess(access.has_access);
      if (access.has_access) {
        const data = await api.getOverlays();
        setOverlays(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Erro ao carregar overlays:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (id) => {
    const vid = videoRefs.current[id];
    if (!vid) return;
    if (playingId === id) {
      vid.pause();
      setPlayingId(null);
    } else {
      Object.values(videoRefs.current).forEach(v => v && v.pause());
      vid.play();
      setPlayingId(id);
    }
  };

  const handleDownload = async (overlay) => {
    try {
      setDownloading(overlay.id);
      await api.downloadOverlay(overlay.id);
    } catch (err) {
      console.error('Erro no download:', err);
    } finally {
      setDownloading(null);
    }
  };

  const filtered = overlays.filter(o => {
    const matchCat = activeCategory === 'all' || o.category === activeCategory;
    const matchSearch = !search || o.title.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24 text-center px-6">
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
          <Lock className="w-7 h-7 text-white/30" />
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">Overlays & Transições</h3>
        <p className="text-white/40 text-sm max-w-xs">
          Disponível para assinantes do plano <strong className="text-white/60">Plataforma + Plugin</strong> ou superior.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-3 mb-4">
          <Layers className="w-5 h-5 text-white/60" />
          <h2 className="text-white font-semibold">Overlays & Transições</h2>
          <span className="text-white/30 text-sm">({filtered.length})</span>
        </div>
        <input
          type="text"
          placeholder="Buscar overlay..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/20"
        />
      </div>

      {/* Categorias */}
      <div className="flex gap-2 px-6 py-3 overflow-x-auto scrollbar-hide border-b border-white/[0.05]">
        {OVERLAY_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
              activeCategory === cat.id
                ? 'bg-white text-black font-medium'
                : 'bg-white/[0.05] text-white/50 hover:text-white hover:bg-white/[0.08]'
            }`}
          >
            <span>{cat.icon}</span>
            <span>{cat.name}</span>
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {filtered.length === 0 ? (
          <div className="text-center text-white/20 py-20 text-sm">
            {overlays.length === 0 ? 'Nenhum overlay cadastrado ainda.' : 'Nenhum resultado encontrado.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(overlay => (
              <div key={overlay.id} className="group relative rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/20 transition-all">
                {/* Preview */}
                <div
                  className="relative aspect-video bg-black cursor-pointer"
                  onClick={() => handlePreview(overlay.id)}
                >
                  {(overlay.video_url || overlay.preview_url) ? (
                    <video
                      ref={el => videoRefs.current[overlay.id] = el}
                      src={overlay.video_url || overlay.preview_url}
                      className="w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      onEnded={() => setPlayingId(null)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Layers className="w-8 h-8 text-white/10" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                    {playingId === overlay.id
                      ? <Pause className="w-8 h-8 text-white" />
                      : <Play className="w-8 h-8 text-white" />
                    }
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-white text-xs font-medium truncate mb-2">{overlay.title}</p>
                  <button
                    onClick={() => handleDownload(overlay)}
                    disabled={downloading === overlay.id}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white/60 hover:text-white text-xs transition-all disabled:opacity-40"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {downloading === overlay.id ? 'Baixando...' : 'Baixar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverlaysView;
